import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  adminResetPassword,
  getAdminRoles,
  getAdminUsers,
  getClientsAdmin,
  inviteClientPortalUser,
  resendPortalInvite,
  sendMagicLoginLink,
  sendPasswordResetLink,
  setAdminUserRoles,
  setAdminUserStatus,
  type AdminRoleDefinition,
  type AdminUserRecord,
  type AdminUsersQuery,
  type AdminUsersResult,
} from "../../lib/api";
import type { ClientRecord } from "../clients/types";
import { Modal } from "../../components/Modal";
import { UserAccountModal } from "./UserAccountModal";
import { RoleGuideModal } from "./RoleGuideModal";
import { useAuth } from "../../auth/AuthContext";
import styles from "./UsersPage.module.css";

/** Lifecycle statuses understood by the package's admin repository. */
const USER_STATUSES = ["Active", "Invited", "Suspended", "Revoked"] as const;

/**
 * The three ways an account can meet the job board, expressed as one choice
 * instead of two independent tick boxes. `jobs` and `jobs-viewer` overlap —
 * ticking both only ever meant `jobs` — so offering them as checkboxes invited
 * a combination that had no meaning.
 */
const JOB_ACCESS_OPTIONS = [
  {
    value: "full",
    role: "jobs",
    label: "Full access",
    description:
      "Create and edit jobs, move cards on the board, add and remove attachments, write quotes and raise invoices.",
  },
  {
    value: "view",
    role: "jobs-viewer",
    label: "View only",
    description:
      "Browse the board, open any job and download attachments — but change nothing.",
  },
  {
    value: "none",
    role: null,
    label: "No job access",
    description: "Can sign in and request a password reset, but sees no job data or job notifications.",
  },
] as const;

type JobAccess = (typeof JOB_ACCESS_OPTIONS)[number]["value"];

/** Role names that mirror the account type or a pack we present as one choice. */
const MANAGED_ROLES = new Set(["admin", "manager", "user", "contractor", "client", "jobs", "jobs-viewer"]);

/** Reads the job-access choice back out of a saved role list. */
function jobAccessOf(roles: string[]): JobAccess {
  if (roles.includes("jobs")) return "full";
  if (roles.includes("jobs-viewer")) return "view";
  return "none";
}

/** Swaps the job-access pack in a role list, leaving every other role alone. */
function withJobAccess(roles: string[], access: JobAccess): string[] {
  const kept = roles.filter((role) => role !== "jobs" && role !== "jobs-viewer");
  const added = JOB_ACCESS_OPTIONS.find((option) => option.value === access)?.role;
  return added ? [...kept, added] : kept;
}

/**
 * Plain-English rendering of what a role list actually permits, resolved
 * through the catalogue the server hands us — so the summary cannot drift from
 * ROLE_CATALOGUE the way a hard-coded blurb would.
 */
function summariseAccess(roles: string[], catalogue: AdminRoleDefinition[]): string[] {
  const granted = new Set<string>();
  for (const definition of catalogue) {
    if (roles.includes(definition.name)) {
      for (const permission of definition.permissions) granted.add(permission);
    }
  }
  const has = (permission: string): boolean => granted.has("*") || granted.has(permission);

  const lines: string[] = [];
  if (has("jobs.write")) {
    lines.push("Create and edit jobs, move cards, manage attachments, write quotes and raise invoices");
  } else if (has("jobs.read")) {
    lines.push("View the job board and download attachments, without changing anything");
  } else {
    lines.push("Nothing on the job board — they will not see any job data");
  }
  if (has("users.manage")) {
    lines.push("Add and suspend user accounts, change access, reset passwords and invite clients");
  } else if (has("users.read")) {
    lines.push("See the list of user accounts");
  }
  if (has("profile.read")) lines.push("View their account and request a password reset");
  return lines;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SortKey = NonNullable<AdminUsersQuery["sort"]>;

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "name", label: "Sort: name" },
  { value: "email", label: "Sort: email" },
  { value: "status", label: "Sort: status" },
  { value: "lastLogin", label: "Sort: last login" },
];

/** One selectable card in an {@link AccessChoice} group. */
interface ChoiceOption {
  value: string;
  label: string;
  description: string;
}

/**
 * A radio group drawn as labelled cards.
 *
 * The dialogs used to offer the whole role catalogue as a row of bare
 * checkboxes — `admin`, `client` and `jobs-viewer` sitting side by side with no
 * hint that the first two duplicate the account type and the last contradicts
 * `jobs`. One choice per question, each carrying its own explanation, removes
 * the guesswork.
 */
function AccessChoice({
  name,
  legend,
  hint,
  options,
  value,
  onChange,
}: {
  name: string;
  legend: string;
  hint?: string;
  options: readonly ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <fieldset className={styles.choiceFieldset}>
      <legend className={styles.choiceLegend}>{legend}</legend>
      {hint && <p className={styles.choiceHint}>{hint}</p>}
      <div className={styles.choiceList}>
        {options.map((option) => (
          <label key={option.value} className={styles.choiceOption}>
            <input
              className={styles.radio}
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span className={styles.choiceLabel}>{option.label}</span>
            <span className={styles.choiceDescription}>{option.description}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** The running answer to “so what will this person actually be able to do?”. */
function AccessSummary({
  roles,
  catalogue,
  title = "This account will be able to",
}: {
  roles: string[];
  catalogue: AdminRoleDefinition[];
  title?: string;
}) {
  const lines = summariseAccess(roles, catalogue);
  if (lines.length === 0) return null;
  return (
    <div className={styles.summary}>
      <span className={styles.summaryTitle}>{title}</span>
      <ul className={styles.summaryList}>
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The roster's one-line answer to "what is this account?", so the column that
 * the Edit dialog opens from speaks the same language as the dialog itself
 * rather than printing raw role names like "user, jobs".
 */
function accessLabel(systemRole: string, roles: string[]): string {
  if (systemRole === "admin") return "Admin · full control";
  if (systemRole === "manager") return "Manager · jobs and users";
  if (systemRole === "client") return "Client portal";
  if (jobAccessOf(roles) === "full") return `${systemRole === "contractor" ? "Contractor" : "Staff"} · full job access`;
  if (jobAccessOf(roles) === "view") return `${systemRole === "contractor" ? "Contractor" : "Staff"} · view only`;
  return `${systemRole === "contractor" ? "Contractor" : "Staff"} · no job access`;
}

/** Formats an ISO timestamp for the roster, with a friendly never-set value. */
function formatDateTime(value: string | null): string {
  if (!value) return "never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

/**
 * Super-admin user management (/admin/users).
 *
 * One roster for every identity: vendor users (admin/manager/user) and client
 * portal invitees (systemRole client, linked to an organisation and able to
 * view/approve quotes on /portal). The package's /auth/admin/users API owns
 * create/status/roles/password-reset; the enriched /api/admin/users list adds
 * portal-link context, and /api/admin/users/:id/resend-invite re-issues
 * set-password links. Everything is gated server-side by `users.manage`; this
 * page additionally hides itself from callers without the permission.
 *
 * Every write is a dialog. They used to be inline panels that pushed the roster
 * down the page while a fourth (reset password) was already a modal, so the same
 * class of action behaved two different ways on one screen.
 */
export function UsersPage() {
  const { canManageUsers } = useAuth();

  const [result, setResult] = useState<AdminUsersResult | null>(null);
  const [roles, setRoles] = useState<AdminRoleDefinition[]>([]);
  const [clients, setClients] = useState<ClientRecord[] | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("name");
  const [page, setPage] = useState(1);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState<AdminUserRecord | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteClientId, setInviteClientId] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDisplayName, setInviteDisplayName] = useState("");
  const [invitePassword, setInvitePassword] = useState("");

  const [resetTarget, setResetTarget] = useState<AdminUserRecord | null>(null);
  const [resetPassword, setResetPassword] = useState("");

  const [rolesEditId, setRolesEditId] = useState<string | null>(null);
  const [rolesDraft, setRolesDraft] = useState<string[]>([]);

  const load = useCallback(async (): Promise<void> => {
    try {
      const body = await getAdminUsers({
        search: appliedSearch,
        status: statusFilter,
        role: roleFilter,
        sort,
        page,
      });
      setResult(body);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load users");
    }
  }, [appliedSearch, statusFilter, roleFilter, sort, page]);

  useEffect(() => {
    if (!canManageUsers) return;
    void load();
  }, [canManageUsers, load]);

  useEffect(() => {
    if (!canManageUsers) return;
    getAdminRoles()
      .then((body) => setRoles(body.roles))
      .catch(() => setRoles([]));
  }, [canManageUsers]);

  // The portal-invite dialog needs the client directory; load it once, lazily.
  useEffect(() => {
    if (!inviteOpen || clients) return;
    getClientsAdmin()
      .then((body) => setClients(body.clients))
      .catch((loadError) =>
        setError(loadError instanceof Error ? loadError.message : "Could not load clients"),
      );
  }, [inviteOpen, clients]);

  const report = (message: string): void => {
    setNotice(message);
    setError(null);
  };

  const runAction = async (action: () => Promise<void>): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };

  const handleStatusChange = (target: AdminUserRecord, status: string): void => {
    if (status === target.status) return;
    void runAction(async () => {
      await setAdminUserStatus(target.id, status);
      report(`${target.email} is now ${status}. Their active sessions were signed out.`);
      await load();
    });
  };

  const openCreate = (): void => {
    setError(null);
    setInviteOpen(false);
    setCreateOpen(true);
  };

  const openInvite = (): void => {
    setError(null);
    setCreateOpen(false);
    setInviteOpen(true);
  };

  const startRolesEdit = (target: AdminUserRecord): void => {
    setError(null);
    setRolesEditId(target.id);
    setRolesDraft([...target.roles]);
  };

  const saveRoles = (target: AdminUserRecord): void => {
    void runAction(async () => {
      await setAdminUserRoles(target.id, rolesDraft);
      setRolesEditId(null);
      report(`Access updated for ${target.email}. They were signed out on every device.`);
      await load();
    });
  };

  const handleResetPasswordSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!resetTarget) return;
    if (resetPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    void runAction(async () => {
      await adminResetPassword(resetTarget.id, resetPassword);
      report(`Password updated for ${resetTarget.email}. Their active sessions were signed out.`);
      setResetTarget(null);
      setResetPassword("");
    });
  };

  const handleResendInvite = (target: AdminUserRecord): void => {
    void runAction(async () => {
      await resendPortalInvite(target.id);
      report(`Invite email sent to ${target.portalLink?.email ?? target.email}.`);
    });
  };

  /** Emails a single-use password-reset link (user sets their own password). */
  const handleSendResetLink = (target: AdminUserRecord): void => {
    void runAction(async () => {
      await sendPasswordResetLink(target.id);
      report(`Password-reset link emailed to ${target.email}. It is valid for one hour.`);
    });
  };

  /** Emails a single-use sign-in link that signs the user straight in. */
  const handleSendMagicLink = (target: AdminUserRecord): void => {
    void runAction(async () => {
      await sendMagicLoginLink(target.id);
      report(`Sign-in link emailed to ${target.email}. It is valid for 15 minutes.`);
    });
  };

  const handleInviteSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (!inviteClientId) {
      setError("Choose the client organisation to invite into.");
      return;
    }
    if (!EMAIL_PATTERN.test(email)) {
      setError("Enter a valid email address.");
      return;
    }
    if (invitePassword && invitePassword.length < 8) {
      setError("Initial password must be at least 8 characters.");
      return;
    }
    void runAction(async () => {
      const outcome = await inviteClientPortalUser(inviteClientId, {
        email,
        displayName: inviteDisplayName.trim(),
        ...(invitePassword ? { password: invitePassword } : {}),
      });
      report(
        outcome.inviteSent
          ? `Invite emailed to ${email}. They will set their own password via the link.`
          : outcome.accountCreated
            ? `Portal account created for ${email}. Share the initial password securely.`
            : `Existing account ${email} linked to the client.`,
      );
      setInviteEmail("");
      setInviteDisplayName("");
      setInvitePassword("");
      setInviteOpen(false);
      setPage(1);
      await load();
    });
  };

  if (!canManageUsers) {
    return (
      <div className={styles.root}>
        <div className={styles.head}>
          <div className={styles.headline}>
            <h1 className={styles.title}>Users</h1>
          </div>
        </div>
        <p className={styles.deny} role="alert">
          You do not have permission to manage users.
        </p>
      </div>
    );
  }

  const total = result?.total ?? 0;
  const pageSize = result?.pageSize ?? 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const roleNames = roles.map((role) => role.name);
  const rolesTarget = rolesEditId
    ? (result?.users.find((entry) => entry.id === rolesEditId) ?? null)
    : null;
  // Admins and managers already hold everything, and a portal account's reach
  // comes from its client link — neither has a job-access choice to make, so
  // the dialog explains itself instead of offering controls that do nothing.
  const accessEditable =
    rolesTarget !== null &&
    rolesTarget.systemRole !== "admin" &&
    rolesTarget.systemRole !== "manager" &&
    rolesTarget.systemRole !== "client";
  // Anything hand-assigned outside the two packs is preserved verbatim on save;
  // the dialog says so rather than silently dropping it.
  const extraRoles = rolesDraft.filter((role) => !MANAGED_ROLES.has(role));
  // While a dialog is open its own copy of the alert is the one the user can
  // see, so the page-level callout stands down rather than reporting twice.
  const dialogOpen = createOpen || detailsTarget !== null || inviteOpen || resetTarget !== null || rolesTarget !== null;

  /** The failure callout, rendered inside whichever surface currently owns it. */
  const errorAlert = error ? (
    <p className={styles.actionError} role="alert">
      {error}
    </p>
  ) : null;

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <div className={styles.headline}>
          <h1 className={styles.title}>Users</h1>
          <p className={styles.lede}>
            Everyone with a login, in one place: staff, contractors, and customers
            invited to the portal to view and approve their quotes. Changing someone&rsquo;s access,
            status or password signs them out on every device.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setGuideOpen(true)}
          >
            Role guide
          </button>
          <button type="button" className={styles.secondaryButton} onClick={openInvite}>
            Invite portal user
          </button>
          <button type="button" className={styles.primaryButton} onClick={openCreate}>
            New user
          </button>
        </div>
      </div>

      <form className={styles.toolbar} onSubmit={handleSearchSubmit} role="search">
        <input
          className={styles.search}
          type="search"
          name="userSearch"
          aria-label="Search users by name, email or business"
          placeholder="Search name, email or business…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.currentTarget.value)}
        />
        <button type="submit" className={styles.secondaryButton}>
          Search
        </button>
        <select
          className={styles.filter}
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(event) => {
            setPage(1);
            setStatusFilter(event.currentTarget.value);
          }}
        >
          <option value="">All statuses</option>
          {USER_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        <select
          className={styles.filter}
          aria-label="Filter by role"
          value={roleFilter}
          onChange={(event) => {
            setPage(1);
            setRoleFilter(event.currentTarget.value);
          }}
        >
          <option value="">All roles and types</option>
          {roleNames.map((role) => (
            <option key={role} value={role}>
              {({ user: "Staff member", contractor: "Contractor", manager: "Manager", admin: "Admin", client: "Client portal", jobs: "Full job access", "jobs-viewer": "View-only job access" } as Record<string, string>)[role] ?? role}
            </option>
          ))}
        </select>
        <select
          className={styles.filter}
          aria-label="Sort users"
          value={sort}
          onChange={(event) => setSort(event.currentTarget.value as SortKey)}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </form>

      {!dialogOpen && errorAlert}
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}

      {result === null ? (
        <p className={styles.empty}>Loading users…</p>
      ) : (
        <>
          <div className={styles.tableFrame}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">User</th>
                  <th scope="col">Status</th>
                  <th scope="col">Access</th>
                  <th scope="col">Portal</th>
                  <th scope="col">Last login</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {result.users.map((entry) => (
                  <tr key={entry.id}>
                    <td data-label="User">
                      <div className={styles.userCell}>
                        <span className={styles.userName}>{entry.displayName || "—"}</span>
                        <span className={styles.userMeta}>
                          {entry.email}
                          {entry.systemRole ? ` · ${entry.systemRole}` : ""}
                        </span>
                        {entry.contractor && <span className={styles.userMeta}>{entry.contractor.businessName || `${entry.contractor.firstName} ${entry.contractor.lastName}`} · {entry.contractor.serviceIds.length} service{entry.contractor.serviceIds.length === 1 ? "" : "s"}</span>}
                        {entry.readOnly && (
                          <span className={styles.badgeMuted} title="Managed outside this screen">
                            read-only
                          </span>
                        )}
                        {entry.isMasterAdmin && (
                          <span
                            className={styles.badgeMuted}
                            title="Provisioned from the environment — this account cannot be deleted, suspended or password-reset from here"
                          >
                            master
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Status">
                      <select
                        className={styles.statusSelect}
                        aria-label={`Status for ${entry.email}`}
                        value={entry.status}
                        disabled={busy || entry.readOnly || entry.isMasterAdmin}
                        title={
                          entry.isMasterAdmin
                            ? "The master admin account is always Active — it cannot be suspended or revoked"
                            : undefined
                        }
                        onChange={(event) => handleStatusChange(entry, event.currentTarget.value)}
                      >
                        {USER_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Access">
                      <div className={styles.rolesCell}>
                        <span
                          className={styles.userMeta}
                          title={entry.roles.length > 0 ? entry.roles.join(", ") : undefined}
                        >
                          {accessLabel(entry.systemRole, entry.roles)}
                        </span>
                        <button
                          type="button"
                          className={styles.textButton}
                          disabled={busy || entry.readOnly || roleNames.length === 0}
                          onClick={() => startRolesEdit(entry)}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                    <td data-label="Portal">
                      {entry.portalLink ? (
                        <div className={styles.portalCell}>
                          <span className={styles.userMeta}>
                            {entry.portalLink.clientName ?? `Client #${entry.portalLink.clientId}`}
                          </span>
                          {entry.portalLink.revokedAt ? (
                            <span className={styles.badgeRevoked}>revoked</span>
                          ) : (
                            <button
                              type="button"
                              className={styles.textButton}
                              disabled={busy}
                              title="Re-send the set-password invite email"
                              onClick={() => handleResendInvite(entry)}
                            >
                              Resend invite
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className={styles.userMeta}>—</span>
                      )}
                    </td>
                    <td data-label="Last login">
                      <span className={styles.lastLogin}>{formatDateTime(entry.lastLoginAt)}</span>
                    </td>
                    <td data-label="Actions">
                      {entry.systemRole === "contractor" && <button type="button" className={styles.textButton} disabled={busy || entry.readOnly} onClick={() => setDetailsTarget(entry)}>Edit details</button>}
                      <button
                        type="button"
                        className={styles.textButton}
                        disabled={busy || entry.readOnly || entry.isMasterAdmin}
                        title={
                          entry.isMasterAdmin
                            ? "The master admin's password belongs to the deployment environment and cannot be reset here"
                            : "Email a single-use link so they can set a new password themselves"
                        }
                        onClick={() => handleSendResetLink(entry)}
                      >
                        Email reset link
                      </button>
                      <button
                        type="button"
                        className={styles.textButton}
                        disabled={busy || entry.readOnly || entry.isMasterAdmin}
                        title={
                          entry.isMasterAdmin
                            ? "The master admin signs in with the deployment credentials"
                            : "Email a single-use link that signs them in directly (valid 15 minutes)"
                        }
                        onClick={() => handleSendMagicLink(entry)}
                      >
                        Email sign-in link
                      </button>
                      <button
                        type="button"
                        className={styles.textButton}
                        disabled={busy || entry.readOnly || entry.isMasterAdmin}
                        title={
                          entry.isMasterAdmin
                            ? "The master admin's password belongs to the deployment environment and cannot be reset here"
                            : undefined
                        }
                        onClick={() => {
                          setResetTarget(entry);
                          setResetPassword("");
                          setError(null);
                        }}
                      >
                        Reset password
                      </button>
                    </td>
                  </tr>
                ))}
                {result.users.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <p className={styles.empty}>No users match the current filters.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pager}>
            <span className={styles.pagerStatus}>
              Page {result.page} of {totalPages} · {total} user{total === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={page <= 1 || busy}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ← Prev
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={page >= totalPages || busy}
              onClick={() => setPage((current) => current + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {(createOpen || detailsTarget) && <UserAccountModal target={detailsTarget ?? undefined} onClose={() => { setCreateOpen(false); setDetailsTarget(null); }} onSaved={(message) => {
        setCreateOpen(false); setDetailsTarget(null); report(message); void load();
      }} />}

      {inviteOpen && (
        <Modal
          title="Invite portal user"
          subtitle="Links an account to a client organisation so they can view and approve quotes."
          onClose={() => setInviteOpen(false)}
          footer={
            <>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setInviteOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="invitePortalUserForm"
                className={styles.primaryButton}
                disabled={busy}
              >
                {busy ? "Working…" : "Send invite"}
              </button>
            </>
          }
        >
          <form
            id="invitePortalUserForm"
            className={styles.formStack}
            onSubmit={(event) => void handleInviteSubmit(event)}
          >
            {errorAlert}
            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span className={styles.label}>Client organisation</span>
                <select
                  className={styles.select}
                  name="inviteClientId"
                  value={inviteClientId}
                  required
                  onChange={(event) => setInviteClientId(event.currentTarget.value)}
                >
                  <option value="">{clients ? "Choose a client…" : "Loading clients…"}</option>
                  {(clients ?? []).map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Email</span>
                <input
                  className={styles.input}
                  type="email"
                  name="inviteEmail"
                  value={inviteEmail}
                  autoComplete="off"
                  required
                  onChange={(event) => setInviteEmail(event.currentTarget.value)}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Display name</span>
                <input
                  className={styles.input}
                  name="inviteDisplayName"
                  value={inviteDisplayName}
                  autoComplete="off"
                  onChange={(event) => setInviteDisplayName(event.currentTarget.value)}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Initial password (optional)</span>
                <input
                  className={styles.input}
                  type="password"
                  name="invitePassword"
                  value={invitePassword}
                  autoComplete="new-password"
                  onChange={(event) => setInvitePassword(event.currentTarget.value)}
                />
              </label>
            </div>
            <p className={styles.hint}>
              Leave the password blank to email a set-password link instead of sharing a password.
            </p>
          </form>
        </Modal>
      )}

      {rolesTarget && (
        <Modal
          title="Change access"
          subtitle={
            rolesTarget.displayName && rolesTarget.displayName !== rolesTarget.email
              ? `${rolesTarget.displayName} · ${rolesTarget.email}`
              : rolesTarget.email
          }
          size="sm"
          onClose={() => setRolesEditId(null)}
          footer={
            accessEditable ? (
              <>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => setRolesEditId(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={busy}
                  onClick={() => saveRoles(rolesTarget)}
                >
                  {busy ? "Working…" : "Save changes"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => setRolesEditId(null)}
              >
                Close
              </button>
            )
          }
        >
          <div className={styles.formStack}>
            {errorAlert}
            <p className={styles.contextLine}>
              <span className={styles.contextLabel}>Account type</span>
              <span>{accessLabel(rolesTarget.systemRole, rolesTarget.roles).split(" · ")[0]}</span>
            </p>

            {accessEditable ? (
              <>
                <AccessChoice
                  name="editJobAccess"
                  legend="What can they do with jobs?"
                  hint="View only and Full access cover all jobs, not only assigned jobs."
                  options={JOB_ACCESS_OPTIONS}
                  value={jobAccessOf(rolesDraft)}
                  onChange={(value) =>
                    setRolesDraft((draft) => withJobAccess(draft, value as JobAccess))
                  }
                />
                <AccessSummary roles={rolesDraft} catalogue={roles} />
                {extraRoles.length > 0 && (
                  <p className={styles.note}>
                    This account also carries {extraRoles.join(", ")}. That is left exactly as it is
                    — only the job access above changes.
                  </p>
                )}
                <p className={styles.hint}>
                  Saving signs this person out on every device. They log straight back in with the
                  new access.
                </p>
              </>
            ) : (
              <>
                <p className={styles.note}>
                  {rolesTarget.systemRole === "client"
                    ? "This is a customer’s portal account. What they can see is decided by the client they are linked to, on the Clients screen — there is nothing to change here."
                    : "Admins and managers already have full access to jobs, so there is nothing to choose here. To reduce what they can do, the account has to be recreated as a staff account."}
                </p>
                {rolesTarget.systemRole !== "client" && (
                  <AccessSummary
                    title="This account can"
                    roles={rolesTarget.roles}
                    catalogue={roles}
                  />
                )}
              </>
            )}
          </div>
        </Modal>
      )}

      {resetTarget && (
        <Modal
          title="Reset password"
          subtitle={resetTarget.email}
          size="sm"
          onClose={() => setResetTarget(null)}
          footer={
            <>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setResetTarget(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="resetPasswordForm"
                className={styles.primaryButton}
                disabled={busy}
              >
                {busy ? "Working…" : "Set password"}
              </button>
            </>
          }
        >
          <form
            id="resetPasswordForm"
            className={styles.formStack}
            onSubmit={(event) => void handleResetPasswordSubmit(event)}
          >
            {errorAlert}
            <label className={styles.field}>
              <span className={styles.label}>New password</span>
              <input
                className={styles.input}
                type="password"
                name="resetPassword"
                value={resetPassword}
                autoComplete="new-password"
                minLength={8}
                required
                onChange={(event) => setResetPassword(event.currentTarget.value)}
              />
            </label>
            <p className={styles.hint}>
              Minimum 8 characters. The user&rsquo;s active sessions are signed out immediately.
            </p>
          </form>
        </Modal>
      )}

      {guideOpen && <RoleGuideModal onClose={() => setGuideOpen(false)} />}
    </div>
  );
}
